import { DateTime } from 'luxon';
import { convertFromIPaginationQueryToIPaginationQueryRequest } from 'types/rest/IPaginationQueryRequest';
import { convertFromISortQueryToISortQueryRequest } from 'types/rest/ISortQueryRequest';
import { IEscrowActualIncomingAchData } from 'types/accounting/data/IEscrowActualIncomingAchData';
import { IEscrowActualIncomingCheckData } from 'types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowActualIncomingWireData } from 'types/accounting/data/IEscrowActualIncomingWireData';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { IEscrowActualIncomingAchQuery } from 'types/accounting/query/IEscrowActualIncomingAchQuery';
import { IEscrowActualIncomingCheckQuery } from 'types/accounting/query/IEscrowActualIncomingCheckQuery';
import { IEscrowActualIncomingWireQuery } from 'types/accounting/query/IEscrowActualIncomingWireQuery';
import { IEscrowExpectedIncomingAchQuery } from 'types/accounting/query/IEscrowExpectedIncomingAchQuery';
import { IEscrowExpectedIncomingCheckQuery } from 'types/accounting/query/IEscrowExpectedIncomingCheckQuery';
import { IEscrowExpectedIncomingWireQuery } from 'types/accounting/query/IEscrowExpectedIncomingWireQuery';
import { IEscrowActualIncomingAchResponse } from 'types/accounting/response/IEscrowActualIncomingAchsResponse';
import { IEscrowActualIncomingCheckResponse } from 'types/accounting/response/IEscrowActualIncomingChecksResponse';
import { IEscrowActualIncomingWireResponse } from 'types/accounting/response/IEscrowActualIncomingWiresResponse';
import {
  IEscrowExpectedAchPullDepositResponse,
  IEscrowMatchAchPullDepositResponse,
} from 'types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { convertISODateRangeToISODateTimeRange } from 'utilities/accounting/convertISODateRangeToISODateTimeRange/convertISODateRangeToISODateTimeRange';

export const convertExpectedAchPullDepositResponseToData = (
  response: IEscrowExpectedAchPullDepositResponse,
  matchedBy?: string
): IEscrowExpectedIncomingAchData => ({
  ...response,
  matchedBy: matchedBy,
});
export const convertAchPullDepositMatchesToData = (
  response: IEscrowMatchAchPullDepositResponse
): IEscrowExpectedIncomingAchData => {
  return convertExpectedAchPullDepositResponseToData(response.expected, response.createdBy);
};
export const convertAchResponseToData = (response: IEscrowActualIncomingAchResponse): IEscrowActualIncomingAchData => ({
  id: response.id,
  createdAt: response.asOfTimestamp,
  wellsFargoTransactionNumber: response.wellsFargoTransactionNumber ?? null,
  source: response.source,
  amountCents: response.amountCents,
  matches: !!response.matches ? response.matches.map(convertAchPullDepositMatchesToData) : undefined,
  accountNumber: response.escrowBankAccount.accountNumber,
  accountName: (() => {
    const base = response.escrowBankAccount.spruceName;
    const nick = response.escrowBankAccount.spruceNickname;
    return nick ? `${base} ${nick}` : base;
  })(),
  accountLegalName: response.escrowBankAccount.legalName,
  achDetail: response.properties,
  fileNumber: response.fileNumber,
});

export const convertACHIncomingActualToApiQuery = ({
  createdAt,
  wellsFargoTransactionNumber,
  source,
  amountCents,
  amountCentsMin,
  matched,
  fileNumber,
  ...rest
}: IEscrowActualIncomingAchQuery) => ({
  asOfTimestampStart: createdAt && convertISODateRangeToISODateTimeRange(createdAt)[0],
  asOfTimestampEnd: createdAt && convertISODateRangeToISODateTimeRange(createdAt)[1],
  wellsFargoTransactionNumber,
  amountCents,
  amountCentsMin,
  source,
  fileNumber,
  matched: matched === undefined || matched === eMatchedQueryValue.ALL ? undefined : matched,
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'],
});

export const convertACHIncomingExpectedToApiQuery = ({
  amountCents,
  amountCentsMax,
  bankAccountName,
  matched,
  wellsFargoTransactionNumber,
  borrowerName,
  createdAt,
  fileNumber,
  escrowFileId,
  propertyAddress,
  enqueuedForDisbursementAt,
  enqueuedForDisbursementAtStart,
  enqueuedForDisbursementAtEnd,
  ...rest
}: IEscrowExpectedIncomingAchQuery) => ({
  createdAtStart: createdAt && createdAt[0],
  createdAtEnd: createdAt && createdAt[1],
  amountCents,
  bankAccountName,
  matched: matched === eMatchedQueryValue.ALL ? undefined : matched,
  wellsFargoTransactionNumber,
  fileNumber,
  escrowFileId,
  borrowerName,
  propertyAddress,
  enqueuedForDisbursementAtStart,
  enqueuedForDisbursementAtEnd,
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT', 'ACH_ACCOUNT'],
});

export const convertCheckActualToApiQuery = ({
  completedAtDate,
  matched,
  amountCents,
  amountCentsMin,
  checkIdentifier,
  checkDetail,
  unmatchedAmountCents,
  wellsFargoPaymentManagerReportType,
  ...rest
}: IEscrowActualIncomingCheckQuery) => ({
  amountCents,
  amountCentsMin,
  checkDetail,
  checkIdentifier,
  wellsFargoPaymentManagerReportType,
  completedAtDateStart: completedAtDate && completedAtDate[0],
  completedAtDateEnd: completedAtDate && completedAtDate[1],
  matched: matched === undefined || matched === eMatchedQueryValue.ALL ? undefined : matched,
  unmatchedAmountCents: unmatchedAmountCents,
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'],
});

export const convertCheckResponseToData = ({
  bankAccount,
  ...data
}: IEscrowActualIncomingCheckResponse): IEscrowActualIncomingCheckData => ({
  ...data,
  unmatchedAmountCents:
    data.amountCents - data.expectedIncomingChecks.reduce((sum, { amountCents }) => sum + amountCents, 0),
  accountNumber: bankAccount.accountNumber,
  accountName: (() => {
    const base = bankAccount.spruceName;
    const nick = bankAccount.spruceNickname;
    return nick ? `${base} ${nick}` : base;
  })(),
  accountLegalName: bankAccount.legalName,
});

export const convertCheckExpectedToApiQuery = ({
  amountCents,
  amountCentsMax,
  bankAccountName,
  borrowerName,
  createdAt,
  fileNumber,
  matched,
  propertyAddress,
  ...rest
}: IEscrowExpectedIncomingCheckQuery) => ({
  amountCents,
  amountCentsMax,
  bankAccountName,
  borrowerName,
  createdAtStart: createdAt && DateTime.fromISO(createdAt[0]).toISO(),
  createdAtEnd: createdAt && createdAt[1] && DateTime.fromISO(createdAt[1]).endOf('day').toISO(),
  fileNumber,
  matched: matched === eMatchedQueryValue.ALL ? undefined : matched,
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  propertyAddress,
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'],
});

export const convertActualWireToApiQuery = ({
  amountCents,
  amountCentsMin,
  completedAtDate,
  matched,
  obi,
  org,
  unmatchedAmountCents,
  ...rest
}: IEscrowActualIncomingWireQuery) => ({
  amountCents,
  amountCentsMin,
  obi,
  unmatchedAmountCents,
  completedAtDateStart: completedAtDate && completedAtDate[0],
  completedAtDateEnd: completedAtDate && completedAtDate[1],
  matched: matched === undefined || matched === eMatchedQueryValue.ALL ? undefined : matched,
  org,
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'],
});

export const convertWireResponseToData = ({
  expectedIncomingWires,
  bankAccount,
  ...rest
}: IEscrowActualIncomingWireResponse): IEscrowActualIncomingWireData => {
  return {
    ...rest,
    expectedIncomingWires: expectedIncomingWires ?? undefined,
    accountNumber: bankAccount?.accountNumber ?? undefined,
    accountName: (() => {
      if (!bankAccount) return '';
      const base = bankAccount.spruceName;
      const nick = bankAccount.spruceNickname;
      return nick ? `${base} ${nick}` : base;
    })(),
    accountLegalName: bankAccount?.legalName ?? undefined,
  };
};

export const convertWireExpectedToApiQuery = ({
  amountCents,
  amountCentsMax,
  bankAccountName,
  borrowerName,
  createdAt,
  fileNumber,
  matched,
  propertyAddress,
  ...rest
}: IEscrowExpectedIncomingWireQuery) => ({
  amountCents,
  amountCentsMax,
  bankAccountName,
  borrowerName,
  fileNumber,
  propertyAddress,
  createdAtStart: createdAt && DateTime.fromISO(createdAt[0]).toISO(),
  createdAtEnd: createdAt && createdAt[1] && DateTime.fromISO(createdAt[1]).endOf('day').toISO(),
  ...convertFromIPaginationQueryToIPaginationQueryRequest(rest),
  ...convertFromISortQueryToISortQueryRequest(rest),
  matched: matched === eMatchedQueryValue.ALL ? undefined : matched,
  includes: ['PARTIES', 'PROPERTY_ADDRESS', 'ACTUAL_ACH_PULL_DEPOSIT', 'MATCHES_ACH_PULL_DEPOSIT'],
});
